import React, { useMemo } from "react";
import { useTable, useSortBy, usePagination, useGlobalFilter } from "react-table";
import CalendarLegend from "../elements/calendar-legend";
import cardMonth from "../elements/card-month";

// ---------- i18n ----------
const dict = {
    en: {
        show: "Show",
        entries: "entries",
        search: "Search",
        date: "Date",
        checks: "Checks",
        failedChecks: "Failed Checks",
        uptime: "Uptime",
        pageXofY: (x, y) => `Page ${x} of ${y}`,
        showing: (from, to, total) => `Showing ${from} to ${to} of ${total} entries`,
        first: "<<",
        prev: "<",
        next: ">",
        last: ">>",
    },
    ru: {
        show: "Показать",
        entries: "записей",
        search: "Поиск",
        date: "Дата",
        checks: "Проверок",
        failedChecks: "Ошибочных проверок",
        uptime: "Аптайм",
        pageXofY: (x, y) => `Страница ${x} из ${y}`,
        showing: (from, to, total) => `Показаны ${from}–${to} из ${total} записей`,
        first: "<<",
        prev: "<",
        next: ">",
        last: ">>",
    },
    ua: {
        show: "Показати",
        entries: "записів",
        search: "Пошук",
        date: "Дата",
        checks: "Перевірок",
        failedChecks: "Невдалих перевірок",
        uptime: "Аптайм",
        pageXofY: (x, y) => `Сторінка ${x} з ${y}`,
        showing: (from, to, total) => `Показано ${from}–${to} з ${total} записів`,
        first: "<<",
        prev: "<",
        next: ">",
        last: ">>",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ---------- Global Search ----------
const GlobalFilter = ({ filter, setFilter, placeholder }) => (
    <div style={{ marginBottom: "10px", textAlign: "right" }}>
        <input
            value={filter || ""}
            onChange={(e) => setFilter(e.target.value || undefined)}
            placeholder={placeholder}
            style={{
                padding: "5px",
                fontSize: "1rem",
                border: "1px solid #ccc",
                borderRadius: "5px",
            }}
        />
    </div>
);

// ---------- Table ----------
const UptimeChecksTable = ({ lang: langProp, monitoringTotal = [], selectedDate }) => {
    const lang = (langProp || getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const data = React.useMemo(() => {
        const mapped = (monitoringTotal || []).map((item) => {
            const totalChecks = Number(item.fail_requests_qty || 0) + Number(item.success_request_qty || 0);
            const key = (() => {
                if (typeof item.valid_date === "string") {
                    const raw = item.valid_date;
                    const parts = raw.includes("T") ? raw.split("T")[0] : raw.split(" ")[0];
                    if (parts) return parts;
                }
                const d = new Date(item.valid_date);
                if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
                return null;
            })();
            return {
                date: item.valid_date,
                dateKey: key,
                checks: totalChecks,
                failedChecks: item.fail_requests_qty,
                uptime: item.uptime || "—",
            };
        });
        if (selectedDate) {
            return mapped.filter((row) => row.dateKey === selectedDate);
        }
        return mapped;
    }, [monitoringTotal, selectedDate]);

    const columns = React.useMemo(
        () => [
            { Header: t.date, accessor: "date", Cell: ({ value }) => new Date(value).toLocaleDateString(lang) },
            { Header: t.checks, accessor: "checks" },
            { Header: t.failedChecks, accessor: "failedChecks" },
            { Header: t.uptime, accessor: "uptime" },
        ],
        [t, lang]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        setGlobalFilter,
        state: { globalFilter, pageIndex, pageSize },
        pageOptions,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        gotoPage,
        setPageSize,
    } = useTable(
        { columns, data, initialState: { pageSize: 10 } },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const from = pageIndex * pageSize + (data.length ? 1 : 0);
    const to = Math.min((pageIndex + 1) * pageSize, data.length);

    return (
        <div style={{ padding: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label>
                    {t.show}{" "}
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        style={{
                            padding: "5px",
                            fontSize: "1rem",
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                        }}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                    </select>{" "}
                    {t.entries}
                </label>
                <GlobalFilter
                    filter={globalFilter}
                    setFilter={setGlobalFilter}
                    placeholder={t.search}
                />
            </div>

            <table
                {...getTableProps()}
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    textAlign: "left",
                    border: "1px solid #ddd",
                    marginTop: "10px",
                }}
            >
                <thead>
                {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                            <th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                style={{
                                    background: "#3f51b5",
                                    color: "white",
                                    padding: "10px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                {column.render("Header")}
                                <span>
                    {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                  </span>
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                    prepareRow(row);
                    return (
                        <tr
                            {...row.getRowProps()}
                            style={{ backgroundColor: row.index % 2 === 0 ? "#f9f9f9" : "#fff" }}
                        >
                            {row.cells.map((cell) => (
                                <td
                                    {...cell.getCellProps()}
                                    style={{ padding: "10px", border: "1px solid #ddd" }}
                                >
                                    {cell.render("Cell")}
                                </td>
                            ))}
                        </tr>
                    );
                })}
                </tbody>
            </table>

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "10px",
                }}
            >
                <span>{t.showing(from, to, data.length)}</span>
                <div>
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        {t.first}
                    </button>{" "}
                    <button onClick={previousPage} disabled={!canPreviousPage}>
                        {t.prev}
                    </button>{" "}
                    <button onClick={nextPage} disabled={!canNextPage}>
                        {t.next}
                    </button>{" "}
                    <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>
                        {t.last}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------- Calendar wrapper ----------
const Calendar = ({ monitoringTotal, selectedDate, onSelectDate }) => {
    const now = new Date();
    const months = [-1, 0, 1].map((offset) => {
        const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });

    const totalsByDate = React.useMemo(() => {
        const map = {};
        (monitoringTotal || []).forEach((item) => {
            if (!item.valid_date) return;
            if (typeof item.valid_date === "string") {
                const raw = item.valid_date;
                const parts = raw.includes("T") ? raw.split("T")[0] : raw.split(" ")[0];
                if (parts) {
                    map[parts] = item;
                    return;
                }
            }
            const d = new Date(item.valid_date);
            if (Number.isNaN(d.getTime())) return;
            const key = d.toISOString().slice(0, 10);
            map[key] = item;
        });
        return map;
    }, [monitoringTotal]);

    const colorResolver = (dateKey) => {
        const item = totalsByDate[dateKey];
        if (!item) return "#9e9e9e";
        const uptimeVal = item.uptime ? parseFloat(String(item.uptime).replace("%", "")) : NaN;
        if (Number.isNaN(uptimeVal)) return { color: "#9e9e9e", title: "No data" };
        if (uptimeVal >= 95) return { color: "#8bc34a", title: `${item.uptime}` };
        if (uptimeVal > 50) return { color: "#ffcc00", title: `${item.uptime}` };
        return { color: "#f44336", title: `${item.uptime || uptimeVal}%` };
    };

    return (
        <div
            style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "20px",
            }}
        >
            {months.map(({ year, month }) => (
                <React.Fragment key={`${year}-${month}`}>{cardMonth(year, month, colorResolver, onSelectDate, selectedDate)}</React.Fragment>
            ))}
        </div>
    );
};

// ---------- Page ----------
const Uptime = ({ lang: langProp, singleArgs }) => {
    const lang = (langProp || getInitialLang());
    const monitoringTotal = singleArgs?.monitoringTotal || [];
    const [selectedDate, setSelectedDate] = React.useState(null);

    const handleSelectDate = (dateKey) => {
        setSelectedDate((prev) => (prev === dateKey ? null : dateKey));
    };

    return (
        <>
            <CalendarLegend lang={lang} />
            <Calendar monitoringTotal={monitoringTotal} selectedDate={selectedDate} onSelectDate={handleSelectDate} />
            <UptimeChecksTable lang={lang} monitoringTotal={monitoringTotal} selectedDate={selectedDate} />
        </>
    );
};

export default Uptime;
